// ─────────────────────────────────────────────────────────────────────────────
// useTheme.ts — Hook for reading and changing the active theme
//
// Keeps the <html> class in sync with the Redux theme state, and registers a
// matchMedia listener so 'system' mode reacts to OS preference changes.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from './index';
import { setTheme, syncSystemTheme } from '@/store/themeSlice';
import type { Theme } from '@/types/cv.types';

/**
 * useTheme — Returns the current theme mode and a toggle function.
 *
 * Usage:
 *   const { mode, resolved, toggleTheme, changeTheme } = useTheme();
 */
export function useTheme() {
  const dispatch = useAppDispatch();
  const mode     = useAppSelector((s) => s.theme.mode);
  const resolved = useAppSelector((s) => s.theme.resolved);

  // Apply the 'dark' CSS class to <html> whenever the resolved theme changes.
  // Tailwind's darkMode:'class' setting reads this class.
  useEffect(() => {
    const root = document.documentElement;
    if (resolved === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [resolved]);

  // Listen for OS colour-scheme changes when the user has chosen 'system'
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');

    const handler = (e: MediaQueryListEvent) => {
      dispatch(syncSystemTheme(e.matches ? 'dark' : 'light'));
    };

    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [dispatch]);

  /**
   * changeTheme — Explicitly set a theme mode (light | dark | system)
   */
  const changeTheme = (newTheme: Theme) => {
    dispatch(setTheme(newTheme));
  };

  /**
   * toggleTheme — Cycles light → dark → system → light
   * Useful for a single-button theme toggle in the navbar.
   */
  const toggleTheme = () => {
    const cycle: Theme[] = ['light', 'dark', 'system'];
    const idx = cycle.indexOf(mode);
    changeTheme(cycle[(idx + 1) % cycle.length]);
  };

  return { mode, resolved, changeTheme, toggleTheme };
}
